import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { environment } from '@env/environment';
import { Contact, ContactAccount, ContactMapping, ContactType } from '@app/model/contact.model';
import { Address } from '@app/model/address.model';
import { catchError, map } from 'rxjs/operators';
import { COUNTRY } from '@app/constants/application-constants';
import { Patches } from '@app/model/contract/core';
import { OrganisationConfig } from '@app/model/organisationConfig';
import { RestURLsEnum } from '@app/model/http-request-enums';
import { CrossBranchSharing } from '@app/model/cross-branch-sharing.model';
import { CommonClient, GetConfigResponse, ProfileClient, GetProfileResponse } from '../clients/client.generated';

export interface ContactDto {
    contactId: number;
    firstName: string;
    lastName: string;
    homePhone: string;
    mobilePhone: string;
    labsysContactId: string;
    inUse: string;
    accountId: string;
    accountName: string;
    primaryEmail: string;
    emails: string;
    leadCompany: string;
    hideMyOrders: boolean;
    contactType: ContactType;
    portalTcAccepted: boolean;
}

export interface ContactPreferenceDto {
    preferenceId: number;
    preferenceType: string;
    preference: any;
}

@Injectable({
    providedIn: 'root'
})
export class ContactService {

    baseUrl = `${environment.csBaseURL}/contact`;

    constructor(private httpClient: HttpClient,
        private profileClient: ProfileClient,
        private commonClient: CommonClient) {
    }

    getContact(access_token?: string): Observable<Contact> {
        return this.profileClient.getProfile().pipe(
            map((dto: GetProfileResponse) => toContactFromProfile(dto))
        );
    }



    getByAuthId(authId: string): Observable<ContactDto> {
        const url = `${this.baseUrl}/by-auth-id/${encodeURIComponent(authId)}`;
        return this.httpClient.get<ContactDto>(url);
    }

    patchByAuthId(authId: string, patches: Patches): Observable<ContactDto> {
        const url = `${this.baseUrl}/by-auth-id/${encodeURIComponent(authId)}`;
        return this.httpClient.patch<ContactDto>(url, patches);
    }

    getPreferences(access_token?: string): Observable<ContactPreferenceDto[]> {
        const url = `${environment.csBaseURL}/preferences`;
        const options = (access_token == null || access_token === undefined)
            ? {}
            : { headers: { Authorization: [`Bearer ${access_token}`] } };
        return this.httpClient.get<ContactPreferenceDto[]>(url, options);
    }

    getDeliveryAddressSuggestions(contactId: number): Observable<Address[]> {
        const url = `${this.baseUrl}/${contactId}/delivery-address-suggestions`;
        return this.httpClient.get<Address[]>(url);
    }

    saveUserPreference(preference: ContactPreferenceDto, access_token?: string): Observable<ContactPreferenceDto> {
        const url = `${environment.csBaseURL}/preferences/update`;
        const options = (access_token == null || access_token === undefined)
            ? {}
            : { headers: { Authorization: [`Bearer ${access_token}`] } };
        return this.httpClient.post<ContactPreferenceDto>(url, preference, options);
    }



    getOrganisationConfig(access_token: string): Observable<OrganisationConfig> {
        const defaultConfig = new OrganisationConfig();

        return this.commonClient.getConfig()
            .pipe(
                map(toOrganisationConfigFromMyLabConfigResponse),
                catchError(error => of(defaultConfig))
            );
    }

}
export const toContact = (dto: Contact): Contact => {
    const contact = new Contact(
        (dto.contactType === null || dto.contactType === undefined) ? 1 : 0,
        dto.contactStatus,
        dto.isStaff || false,
        dto.firstName || '',
        dto.lastName || '',
        dto.phone || '',
        dto.email || '',
        dto.hideMyOrders || false,
        dto.postalAddress || createEmptyAddress(),
        dto.deliveryAddress || createEmptyAddress()
    );
    contact.organisation = { id: dto.organisation.id, name: dto.organisation.name || '' };
    contact.mobile = dto.mobile;
    contact.portalTcAccepted = dto.portalTcAccepted;
    return contact;

};

export const toContactFromProfile = (dto: GetProfileResponse): Contact => {
    const postal = dto.postalAddress
        ? new Address(
            dto.postalAddress.recipientInfo || '',
            dto.postalAddress.line1 || '',
            dto.postalAddress.line2 || '',
            dto.postalAddress.townOrCity || '',
            dto.postalAddress.province || '',
            dto.postalAddress.postCode || '',
            dto.postalAddress.country || '',
            dto.postalAddress.nzPostDpId
        )
        : createEmptyAddress();

    const delivery = dto.deliveryAddress
        ? new Address(
            dto.deliveryAddress.recipientInfo || '',
            dto.deliveryAddress.line1 || '',
            dto.deliveryAddress.line2 || '',
            dto.deliveryAddress.townOrCity || '',
            dto.deliveryAddress.province || '',
            dto.deliveryAddress.postCode || '',
            dto.deliveryAddress.country || '',
            dto.deliveryAddress.nzPostDpId
        )
        : createEmptyAddress();

    const contact = new Contact(
        (dto.contactType === null || dto.contactType === undefined) ? 1 : 0,
        (dto.contactStatus as any),
        dto.isStaff || false,
        dto.firstName || '',
        dto.lastName || '',
        dto.phone || '',
        dto.email || '',
        dto.hideMyOrders || false,
        postal,
        delivery
    );

    const orgId = dto.organisation?.id ? parseInt(dto.organisation.id, 10) : 0;
    contact.organisation = { id: Number.isNaN(orgId) ? 0 : orgId, name: dto.organisation?.name || '' };
    contact.mobile = dto.mobile || '';
    contact.portalTcAccepted = dto.portalTcAccepted || false;
    contact.contactId = dto.contactId ?? 0;
    contact.inUse = dto.inUse ?? false;
    return contact;
};

const createEmptyAddress = () => new Address(
    '', '', '', '', '', '', COUNTRY
);

export const toOrganisationConfigFromMyLabConfigResponse = (dto: GetConfigResponse): OrganisationConfig => {
    const config = new OrganisationConfig();
    const jobsCanCrossBranchShare = dto.jobsCanCrossBranchShare ?? true;

    config.canOrderSamplingSupplies = dto.canOrderSamplingSupplies ?? true;
    config.canOrderLabTests = dto.canOrderLabTests ?? true;
    config.crossBranchSharing = null;
    config.effectiveCrossBranchSharing = jobsCanCrossBranchShare
        ? CrossBranchSharing.Enabled
        : CrossBranchSharing.Disabled;

    return config;
};
